import type {
  BaseWorkspaceRepository,
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
} from "./BaseWorkspaceRepository.interface";
import { localApiBase } from "../../../lib/localApi";

class ApiWorkspaceRepository implements BaseWorkspaceRepository {
  private get Domain(): string {
    return localApiBase();
  }

  async select() {
    try {
      const response = await fetch(`${this.Domain}/api/workspaces/select`);
      const result = await response.json();
      return result;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getCurrent() {
    try {
      const response = await fetch(`${this.Domain}/api/workspace`);
      const result = await response.json();
      return result.data;
    } catch (e) {
      console.error(e);
      return "";
    }
  }

  async create(workspace: CreateWorkspaceRequest): Promise<{
    error: boolean;
    message: string;
    data?: CreateWorkspaceResponse;
  }> {
    try {
      const hasBlobs = workspace.logo instanceof Blob || workspace.cover instanceof Blob;

      let response: Response;

      if (hasBlobs) {
        const formData = new FormData();
        formData.append("path", workspace.path);
        if (workspace.name) formData.append("name", workspace.name);
        if (workspace.description) formData.append("description", workspace.description);

        // Always send isPublic value (as "true" or "false" string)
        // The backend's @Transform decorator will correctly parse it
        formData.append("isPublic", workspace.isPublic ? "true" : "false");

        formData.append("selected", String(!!workspace.selected));

        if (workspace.logo instanceof Blob) {
          formData.append("logo", workspace.logo, "logo.png");
        } else if (workspace.logo) {
          formData.append("logo", workspace.logo);
        }

        if (workspace.cover instanceof Blob) {
          formData.append("cover", workspace.cover, "cover.png");
        } else if (workspace.cover) {
          formData.append("cover", workspace.cover);
        }

        response = await fetch(`${this.Domain}/api/workspaces`, {
          method: "POST",
          body: formData,
        });
      } else {
        response = await fetch(`${this.Domain}/api/workspaces`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(workspace),
        });
      }

      const result = await response.json();

      if (response.ok && result.data) {
        return {
          error: false,
          message: "Workspace created successfully",
          data: result.data as CreateWorkspaceResponse,
        };
      }
      return {
        error: true,
        message: result.message || result.error || "Error creating workspace",
      };
    } catch (e) {
      console.error("Error creating workspace:", e);
      return {
        error: true,
        message: e instanceof Error ? e.message : "Error connecting to create workspace",
      };
    }
  }

  async delete(workspaceId: string): Promise<{ error: boolean; message: string }> {
    try {
      const response = await fetch(`${this.Domain}/api/workspaces/${workspaceId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          return {
            error: false,
            message: result.message || "Workspace deleted successfully",
          };
        }
      }

      const result = await response.json();
      return {
        error: true,
        message: result.message || "Error deleting workspace",
      };
    } catch (e) {
      console.error("Error deleting workspace:", e);
      return {
        error: true,
        message: "Error connecting to delete workspace",
      };
    }
  }

  async import(path: string): Promise<{
    error: boolean;
    message: string;
    /** Código estable del API (p.ej. NOT_ERIOL_PROJECT); el caller lo localiza. */
    code?: string;
    data?: CreateWorkspaceResponse;
  }> {
    try {
      const response = await fetch(`${this.Domain}/api/workspaces/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ path }),
      });

      const result = await response.json();

      if (response.ok && result.data) {
        return {
          error: false,
          message: "Workspace imported successfully",
          data: result.data as CreateWorkspaceResponse,
        };
      }
      return {
        error: true,
        message: result.message || "Error importing workspace",
        code: typeof result.code === "string" ? result.code : undefined,
      };
    } catch (e) {
      console.error("Error importing workspace:", e);
      return {
        error: true,
        message: "Error connecting to import workspace",
      };
    }
  }

  async unselect(): Promise<{ error: boolean; message: string }> {
    try {
      const response = await fetch(`${this.Domain}/api/workspaces/unselect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          return {
            error: false,
            message: result.message || "All workspaces unselected successfully",
          };
        }
      }

      const result = await response.json();
      return {
        error: true,
        message: result.message || "Error unselecting workspaces",
      };
    } catch (e) {
      console.error("Error unselecting workspaces:", e);
      return {
        error: true,
        message: "Error connecting to unselect workspaces",
      };
    }
  }
}

export default ApiWorkspaceRepository;
